import { useState } from "react";
import { X, Loader2, KeyRound } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { toast } from "../../components/ui/sonner";

export default function ChangePasswordModal({ onClose }) {
  const { authFetch, logout } = useAdminAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast("Password too short", { description: "Use at least 8 characters." });
      return;
    }
    if (next !== confirm) {
      toast("Passwords don't match");
      return;
    }
    setSaving(true);
    try {
      await authFetch("/api/admin/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      toast("Password updated", { description: "Please sign in again with your new password." });
      logout();
    } catch (err) {
      toast("Update failed", { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00B4D8]/60";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4">
      <div data-testid="change-password-modal" className="w-full max-w-md rounded-3xl border border-white/12 bg-[#0f2b4a] p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-[#00B4D8]" />
            <h3 className="font-display font-bold text-lg">Change password</h3>
          </div>
          <button data-testid="change-password-close" onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs text-slate-400">Current password</span>
            <input
              data-testid="pw-current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={inputCls}
              required
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-400">New password (min 8 chars)</span>
            <input
              data-testid="pw-new"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={inputCls}
              minLength={8}
              required
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-400">Confirm new password</span>
            <input
              data-testid="pw-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputCls}
              required
            />
          </label>
          <button
            data-testid="pw-submit"
            type="submit"
            disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#4CAF50] px-6 py-2.5 text-sm font-semibold text-[#04140a] hover:brightness-110 disabled:opacity-60"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
            {saving ? "Updating…" : "Update password"}
          </button>
          <p className="text-[11px] text-slate-500 text-center">
            You&apos;ll be signed out after saving. Sign back in with the new password.
          </p>
        </form>
      </div>
    </div>
  );
}
